import React from 'react';
import {
  Paper,
  TextField,
  Box,
  Grid,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { EventFilters } from '@/hooks/useEvents';
import { PrimaryButton, SecondaryButton } from '@/components/index';

export interface EventsFiltersProps {
  filters: EventFilters;
  appliedFilters: EventFilters;
  onFiltersChange: (filters: EventFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  hasUnappliedChanges: () => boolean;
}

export const EventsFilters: React.FC<EventsFiltersProps> = ({
  filters,
  appliedFilters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  hasUnappliedChanges,
}) => {
  const handleFieldChange = (field: keyof EventFilters, value: string | Date | null) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  return (
    <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DateTimePicker
            label="Start Date"
            value={filters.startAt}
            onChange={(newValue) => handleFieldChange('startAt', newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DateTimePicker
            label="End Date"
            value={filters.endAt}
            onChange={(newValue) => handleFieldChange('endAt', newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            size="small"
            label="Event Code"
            fullWidth
            value={filters.code}
            onChange={(e) => handleFieldChange('code', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            size="small"
            label="Event Type"
            fullWidth
            value={filters.type}
            onChange={(e) => handleFieldChange('type', e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasUnappliedChanges() && (
            <Chip
              label="Unapplied filters"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          {(appliedFilters.startAt || appliedFilters.endAt || appliedFilters.code || appliedFilters.type) && (
            <Chip
              label="Filtered"
              color="info"
              size="small"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <SecondaryButton onClick={onClearFilters}>
            Clear Filters
          </SecondaryButton>
          <PrimaryButton
            onClick={onApplyFilters}
            color={hasUnappliedChanges() ? "warning" : "primary"}
          >
            Apply Filters
          </PrimaryButton>
        </Box>
      </Box>
    </Paper>
  );
};
